import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import SavedDocIcon from "../../../img/saved-doc.svg";
import useSWR from "swr";
import { KPIDashboardService } from "../service/kpiDashboardService";
import CustomSelect from "../../Shared/CustomSelect/CustomSelect";
import ErrorCard from "../../InventoryManagement/components/Common/ErrorCard";

const toMonthLabel = (monthValue = "") => {
  const safeValue = String(monthValue).trim().toLowerCase();
  if (!safeValue) return "";
  return safeValue.charAt(0).toUpperCase() + safeValue.slice(1);
};

const toCompactNumber = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  const numericValue = Number(value);
  const absValue = Math.abs(numericValue);

  if (absValue >= 1000000000) {
    return `${(numericValue / 1000000000).toFixed(absValue >= 10000000000 ? 0 : 1)}B`;
  }
  if (absValue >= 1000000) {
    return `${(numericValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
  }
  if (absValue >= 1000) {
    return `${(numericValue / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
  }
  return `${numericValue}`;
};

const getRoundedAxisMax = (maxValue) => {
  if (!maxValue || maxValue <= 0) return 10;

  const magnitude = 10 ** Math.floor(Math.log10(maxValue));
  return Math.ceil(maxValue / magnitude) * magnitude;
};

const getGrowthTrend = (percentage) => {
  if (!percentage) return "neutral";
  return percentage >= 0 ? "positive" : "negative";
};

const KPIPerformanceGraph = ({ userId, metric = null }) => {
  const [selectedMetric, setSelectedMetric] = useState(metric || "overall");
  const showTargetSeries = selectedMetric !== "overall";

  const { data: performanceData, error } = useSWR(
    `${userId}_${selectedMetric}`,
    () => {
      return KPIDashboardService.ytdPerformanceFetcher({
        userId,
        metric: selectedMetric === "overall" ? null : selectedMetric,
      });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data: metricsData, isValidating: isMetricsLoading } = useSWR(
    "kpi-metrics-list",
    () => KPIDashboardService.metricListFetcher(),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const metricOptions = [
    { name: "Overall", id: "overall" },
    ...(metricsData?.metrics || []).map((m) => ({
      name: m.label,
      id: m.metrics,
    })),
  ];

  const loading = !performanceData && !error;

  const handleMetricChange = (e) => {
    setSelectedMetric(e.target.value);
  };

  const normalizedRows = useMemo(() => {
    if (!performanceData || !Array.isArray(performanceData.data)) return [];

    return performanceData.data.map((item) => ({
      month: toMonthLabel(item.month),
      target: Number(item.target) || 0,
      achievement: Number(item.achievement) || 0,
      achievementPercentage: item.ach_pct,
      growthPercentage: item.growth_pct,
    }));
  }, [performanceData]);

  const categories = useMemo(
    () => normalizedRows.map((item) => item.month),
    [normalizedRows]
  );

  const achievements = useMemo(
    () => normalizedRows.map((item) => item.achievement),
    [normalizedRows]
  );

  const targets = useMemo(
    () => normalizedRows.map((item) => item.target),
    [normalizedRows]
  );

  const chartMax = useMemo(() => {
    const largestValue = Math.max(...achievements, ...targets, 0);
    return getRoundedAxisMax(largestValue);
  }, [achievements, targets]);

  const series = useMemo(() => {
    const chartSeries = [
      {
        name: "Achievements",
        data: achievements,
      },
    ];

    if (showTargetSeries) {
      chartSeries.push({
        name: "Targets",
        data: targets,
      });
    }

    return chartSeries;
  }, [achievements, showTargetSeries, targets]);

  const weightageLabel =
    performanceData?.metric_weightage == null
      ? "N/A Weightage"
      : `${performanceData.metric_weightage}% Weightage`;

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: {
          show: false,
        },
        animations: {
          speed: 320,
        },
      },
      colors: ["#E2136E", "#B9B9BC"],
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "#e4e7ef",
        strokeDashArray: 0,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 0,
          columnWidth: "42%",
        },
      },
      stroke: {
        show: false,
      },
      xaxis: {
        categories,
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        labels: {
          style: {
            colors: "#6B7280",
            fontSize: "12px",
            fontFamily: "Inter, sans-serif",
          },
        },
      },
      yaxis: {
        min: 0,
        max: chartMax,
        tickAmount: 4,
        labels: {
          style: {
            colors: "#6B7280",
            fontSize: "12px",
            fontFamily: "Inter, sans-serif",
          },
          formatter: (value) => toCompactNumber(value),
        },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const monthData = normalizedRows[dataPointIndex] || {};
          const seriesName =
            w?.globals?.seriesNames?.[seriesIndex]?.toLowerCase() || "";
          const isTargetBar = seriesName === "targets";

          const primaryLabel = isTargetBar ? "Target" : "Achievement";
          const primaryValue = isTargetBar
            ? monthData.target
            : monthData.achievement;
          const monthLabel = categories[dataPointIndex] || "";
          const achievementPercentage =
            monthData.achievementPercentage !== null
              ? `${monthData.achievementPercentage.toFixed(2)}%`
              : "N/A";
          const growthPercentage =
            monthData.growthPercentage !== null
              ? `${monthData.growthPercentage.toFixed(2)}%`
              : "N/A";

          return `
            <div class="kpi-performance-tooltip">
              <div class="tooltip-title">${monthLabel}</div>
              <div class="tooltip-row">
                <span class="tooltip-label">${primaryLabel}</span>
                <span class="tooltip-value">${toCompactNumber(primaryValue)}</span>
              </div>
              <div class="tooltip-row">
                <span class="tooltip-label">Achievement Percentage</span>
                <span class="tooltip-value">${achievementPercentage}</span>
              </div>
              <div class="tooltip-row">
                <span class="tooltip-label">Growth Percentage</span>
                <span class="tooltip-value ${getGrowthTrend(monthData.growthPercentage)}">${growthPercentage}</span>
              </div>
            </div>
          `;
        },
      },
      legend: {
        show: false,
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            legend: {
              show: false,
            },
          },
        },
      ],
    }),
    [categories, chartMax, normalizedRows]
  );

  const legends = useMemo(() => {
    const baseLegends = [
      {
        label: "Achievements",
        color: "#E2136E",
      },
    ];

    if (showTargetSeries) {
      baseLegends.push({
        label: "Targets",
        color: "#B9B9BC",
      });
    }

    return baseLegends;
  }, [showTargetSeries]);

  return (
    <div className="kpi-performance-graph-card">
      <div className="kpi-performance-header">
        <h3>Month Wise KPI Achievement Tracker</h3>
        <div className="kpi-performance-graph-legends">
          {legends.map((legend, index) => (
            <div key={index} className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: legend.color }}
              />
              <span className="legend-label">{legend.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="kpi-performance-controls">
        <CustomSelect
          item={{
            key: "metric",
            value: selectedMetric,
            options: isMetricsLoading ? [] : metricOptions,
            icon: SavedDocIcon,
          }}
          isMultiSelect={false}
          isRequired={false}
          handleChange={handleMetricChange}
          style={{
            width: "300px",
            boxShadow: "none",
            height: "48px",
            border: "1px solid #F2F2F2B8",
          }}
          isSearchable={false}
          showExpandIndicator={true}
        />
        <div className="kpi-performance-weightage" title={weightageLabel}>
          <span>{weightageLabel}</span>
        </div>
      </div>
      {error ? (
        <ErrorCard
          title="Error Loading Performance Data"
          message="Failed to load performance data. Please try again later."
        />
      ) : loading ? (
        <ChartSkeleton />
      ) : (
        <Chart options={chartOptions} series={series} type="bar" height={320} />
      )}
    </div>
  );
};
const ChartSkeleton = () => (
  <div className="chart-skeleton">
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="skeleton-bar"
        style={{ height: `${Math.random() * 60 + 20}%` }}
      />
    ))}
  </div>
);
export default KPIPerformanceGraph;
